import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';

// Define types for realtor data
export interface Realtor {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  image?: string;
  joinDate: string;
  properties: number;
  commission: number;
  affiliates: Affiliate[];
  commissionHistory: CommissionRecord[]; // Add commissionHistory
  password?: string; // Add password as optional for registration
  affiliateLink?: string; // Add affiliateLink
}

export interface Affiliate {
  id: string;
  name: string;
  email: string;
  phone?: string; // Make optional as it might not always be present from backend
  image?: string;
  level: 1 | 2; // This might need to be inferred or set on frontend if backend doesn't provide
  joinDate: string;
  properties: number;
  password?: string; // Added to resolve type error, assuming it's optional from backend
  commission: number;
  referredBy?: string; // ID of the realtor who referred them (upline_id from backend)
}

export interface CommissionRecord {
  id: string;
  realtorId: string;
  propertyId: string; // Add propertyId
  propertyTitle: string; // Add propertyTitle
  downlinerId?: string; // Optional, as it can be null for direct sales
  amount: number;
  commissionType: 'direct_sale' | 'level1_downliner' | 'level2_downliner';
  status: 'pending' | 'paid' | 'cancelled';
  date: string; // created_at from backend
  realtorName: string; // From join
  downlinerName?: string; // From join, optional
}

// Define the interface for the raw data received from the backend for a Realtor profile
interface RawRealtorData {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  image?: string;
  created_at: string; // Backend field for joinDate
  properties_count?: number; // Backend field for properties
  commission_amount?: number; // Backend field for commission
  affiliates?: Affiliate[]; // Assuming backend sends this directly
  commission_history?: CommissionRecord[]; // Backend field for commissionHistory
  affiliate_link?: string; // Backend field for affiliateLink
}

interface RawAffiliateData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  image?: string;
  created_at: string;
  upline_id?: string;
}

// Create context for realtor management
interface RealtorContextType {
  realtors: Realtor[];
  affiliates: Affiliate[];
  currentUser: Realtor | null;
  loading: boolean;
  error: string | null;
  commissions: CommissionRecord[]; // Add commissions state
  
  // Authentication actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  
  // Realtor actions
  addRealtor: (realtor: Omit<Realtor, 'id' | 'properties' | 'commission' | 'affiliates' | 'affiliateLink'> & { password?: string }, profileImage?: File | null, referredBy?: string | null) => Promise<void>;
  updateRealtor: (id: string, data: Partial<Realtor>) => Promise<void>;
  deleteRealtor: (id: string) => Promise<void>;
  
  // Affiliate actions
  addAffiliate: (affiliate: Omit<Affiliate, 'id' | 'properties' | 'commission' | 'joinDate' | 'level'> & { password?: string }) => Promise<void>;
  updateAffiliate: (id: string, data: Partial<Omit<Affiliate, 'properties' | 'commission' | 'joinDate' | 'level'>>) => Promise<void>;
  deleteAffiliate: (id: string) => Promise<void>;
  
  // Commission actions
  fetchCommissions: () => Promise<void>; // New function to fetch commissions
  addCommission: (commission: Omit<CommissionRecord, 'id'>) => Promise<void>; // Update addCommission
  updateCommissionStatus: (id: string, status: 'pending' | 'paid' | 'cancelled') => Promise<void>;
  
  // Dashboard data
  getTotalCommission: (realtorId: string) => number;
  getMonthlyCommission: (realtorId: string, month: number, year: number) => number;
  getAffiliatesByLevel: (realtorId: string, level: 1 | 2) => Affiliate[];
}

// Create the context with default values
const RealtorContext = React.createContext<RealtorContextType>({
  realtors: [],
  affiliates: [],
  currentUser: null,
  loading: false,
  error: null,
  commissions: [], // Default commissions state
  
  login: async () => {},
  logout: () => {},
  
  addRealtor: async () => { },
  updateRealtor: async () => {},
  deleteRealtor: async () => {},
  
  addAffiliate: async () => {},
  updateAffiliate: async () => {},
  deleteAffiliate: async () => {},
  
  fetchCommissions: async () => {}, // Default for new function
  addCommission: async () => {},
  updateCommissionStatus: async () => {},
  
  getTotalCommission: () => 0,
  getMonthlyCommission: () => 0,
  getAffiliatesByLevel: () => [],
});

// Custom hook to use the RealtorContext
export const useRealtorContext = () => {
  const context = React.useContext(RealtorContext);
  if (context === undefined) {
    throw new Error('useRealtorContext must be used within a RealtorProvider');
  }
  return context;
};

// Define the expected login response type
interface LoginResponseData {
  token: string;
  // Add other fields if your backend returns more (e.g., user info)
}

// Provider component
export const RealtorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [realtors, setRealtors] = useState<Realtor[]>([]);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [currentUser, setCurrentUser] = useState<Realtor | null>(null); // Default to null
  const [loading, setLoading] = useState<boolean>(true); // Set to true initially
  const [error, setError] = useState<string | null>(null);
  const [commissions, setCommissions] = useState<CommissionRecord[]>([]); // New state for commissions

  // Fetch affiliates from backend
  const fetchAffiliates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      const response = await axios.get<RawAffiliateData[]>('https://uniquestatebackend.gomsolutions.com.ng/api/affiliates', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log('Fetched affiliates data:', response.data); // Log the typed data
      setAffiliates(response.data.map((aff: RawAffiliateData) => ({ // Explicitly type 'aff'
        ...aff,
        joinDate: aff.created_at ? new Date(aff.created_at).toISOString().split('T')[0] : '',
        properties: 0, // Assuming properties and commission are not directly from this endpoint
        commission: 0,
        referredBy: aff.upline_id,
        level: 1, // Assuming all fetched are level 1 for now, adjust if backend provides
      })));
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    }
  }, []);

  // Check for existing token on mount
  useEffect(() => {
    const loadUserFromStorage = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const profileResponse = await axios.get<RawRealtorData>(`https://uniquestatebackend.gomsolutions.com.ng/api/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const profileData = profileResponse.data as RawRealtorData; // Explicitly cast to RawRealtorData
          const fetchedUser: Realtor = {
            id: profileData.id,
            name: profileData.name,
            email: profileData.email,
            phone: profileData.phone,
            role: profileData.role,
            image: profileData.image ? `https://uniquestatebackend.gomsolutions.com.ng${profileData.image}` : undefined,
            joinDate: profileData.created_at ? new Date(profileData.created_at).toISOString().split('T')[0] : '', // Map created_at to joinDate
            properties: profileData.properties_count || 0, // Map properties_count to properties
            commission: profileData.commission_amount || 0, // Map commission_amount to commission
            affiliates: profileData.affiliates || [],
            commissionHistory: profileData.commission_history || [], // Map commission_history to commissionHistory
            affiliateLink: profileData.affiliate_link, // Map affiliate_link to affiliateLink
          };
          setCurrentUser(fetchedUser);
          fetchAffiliates(); // Fetch affiliates when user is loaded
        }
      } catch (err: any) {
        console.error('Error loading user from storage:', err);
        localStorage.removeItem('token');
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);
  
  // Authentication actions
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post<LoginResponseData>('https://uniquestatebackend.gomsolutions.com.ng/api/login', { email, password });
      console.log('Login response data:', response.data); // Log the typed data

      const data: LoginResponseData = response.data;
      console.log('Login data after explicit typing:', data);

      // No need for response.ok check with axios, it throws for non-2xx

      localStorage.setItem('token', data.token);
      // After successful login, fetch full user profile including affiliates
      const profileResponse = await axios.get<RawRealtorData>(`https://uniquestatebackend.gomsolutions.com.ng/api/profile`, {
        headers: {
          'Authorization': `Bearer ${data.token}`
        }
      });

          const profileData = profileResponse.data as RawRealtorData; // Explicitly cast to RawRealtorData
          const fetchedUser: Realtor = {
            id: profileData.id,
            name: profileData.name,
            email: profileData.email,
            phone: profileData.phone,
            role: profileData.role,
            image: profileData.image ? `https://uniquestatebackend.gomsolutions.com.ng${profileData.image}` : undefined,
            joinDate: profileData.created_at ? new Date(profileData.created_at).toISOString().split('T')[0] : '', // Map created_at to joinDate
            properties: profileData.properties_count || 0, // Map properties_count to properties
            commission: profileData.commission_amount || 0, // Map commission_amount to commission
            affiliates: profileData.affiliates || [],
            commissionHistory: profileData.commission_history || [], // Map commission_history to commissionHistory
            affiliateLink: profileData.affiliate_link, // Map affiliate_link to affiliateLink
          };
          setCurrentUser(fetchedUser);
          fetchAffiliates(); // Fetch affiliates after successful login
      } catch (err: any) { // Catch for axios.post login
        setError(err.response?.data?.message || err.message || 'Login failed');
        toast.error(err.response?.data?.message || err.message || 'Login failed');
        console.error('Login error:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    };
  
  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('token');
  };
  
  // Realtor actions
  const addRealtor = async (realtor: Omit<Realtor, 'id' | 'properties' | 'commission' | 'affiliates' | 'commissionHistory' | 'affiliateLink'> & { password?: string }, profileImage?: File | null, referredBy?: string | null, selectedSvg?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      let response;
      const commonBody = {
        name: realtor.name,
        email: realtor.email,
        password: realtor.password,
        role: 'realtor',
        phone: realtor.phone,
        referredBy: referredBy, // Include referredBy
        selectedSvg: selectedSvg, // Include selected SVG
      };

      if (profileImage) {
        const formData = new FormData();
        for (const key in commonBody) {
          if (commonBody[key as keyof typeof commonBody] !== undefined) {
            formData.append(key, commonBody[key as keyof typeof commonBody] as string | Blob);
          }
        }
        formData.append('image', profileImage);

        response = await fetch('https://uniquestatebackend.gomsolutions.com.ng/api/register', {
          method: 'POST',
          body: formData,
        });
      } else {
        response = await fetch('https://uniquestatebackend.gomsolutions.com.ng/api/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(commonBody),
        });
      }

      // Check if the response is OK before attempting to parse JSON
      if (!response.ok) {
        const errorData = await response.text(); // Read as text to avoid JSON parsing error
        throw new Error(errorData || 'Failed to register realtor');
      }

      const data: RawRealtorData = await response.json();
      console.log('Add Realtor data after explicit typing:', data);

      const newRealtor: Realtor = {
        ...realtor,
        id: data.id || `r${realtors.length + 1}`,
        properties: 0,
        commission: 0,
        affiliates: [],
        commissionHistory: [], // Initialize commissionHistory
        affiliateLink: `${window.location.origin}/affiliate/register?referredBy=${data.id || `r${realtors.length + 1}`}`,
        image: data.image ? `https://uniquestatebackend.gomsolutions.com.ng${data.image}` : undefined, // Use image from backend response
      };
      
      setRealtors([...realtors, newRealtor]);

      // If referredBy is present, add the new realtor as an affiliate to the referrer
      if (referredBy) {
        const referrer = realtors.find(r => r.id === referredBy);
        if (referrer) {
          const newAffiliate: Affiliate = {
            id: newRealtor.id,
            name: newRealtor.name,
            email: newRealtor.email,
            phone: newRealtor.phone,
            image: newRealtor.image, // This will already be the full URL
            level: 1, // Assuming direct referrals are Level 1
            joinDate: newRealtor.joinDate,
            properties: 0,
            commission: 0,
            referredBy: referrer.id,
          };
          setAffiliates(prev => [...prev, newAffiliate]);
          setRealtors(prevRealtors => prevRealtors.map(r =>
            r.id === referrer.id ? { ...r, affiliates: [...r.affiliates, newAffiliate] } : r
          ));
        }
      }

    } catch (err: any) {
      setError(err.message || 'Failed to add realtor');
      console.error('Add realtor error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const updateRealtor = async (id: string, data: Partial<Realtor>) => {
    setLoading(true);
    try {
      // In a real app, this would be an API call
      const updatedRealtors = realtors.map(realtor =>
        realtor.id === id ? { ...realtor, ...data } : realtor
      );
      
      setRealtors(updatedRealtors);
      
      // Update current user if it's the one being updated
      if (currentUser && currentUser.id === id) {
        setCurrentUser({ ...currentUser, ...data });
      }
    } catch (err) {
      setError('Failed to update realtor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const deleteRealtor = async (id: string) => {
    setLoading(true);
    try {
      // In a real app, this would be an API call
      const updatedRealtors = realtors.filter(realtor => realtor.id !== id);
      setRealtors(updatedRealtors);
      
      // Clear current user if it's the one being deleted
      if (currentUser && currentUser.id === id) {
        setCurrentUser(null);
      }
    } catch (err) {
      setError('Failed to delete realtor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // Affiliate actions
  const addAffiliate = async (affiliate: Omit<Affiliate, 'id' | 'properties' | 'commission' | 'joinDate' | 'level'> & { password?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found for addAffiliate.');
        throw new Error('No authentication token found.');
      }
      console.log('Token being sent for addAffiliate:', token); // Log the token

      await axios.post('https://uniquestatebackend.gomsolutions.com.ng/api/affiliates', {
        name: affiliate.name,
        email: affiliate.email,
        phone: affiliate.phone,
        password: affiliate.password || 'default_password', // Provide a default or handle password creation
        image: affiliate.image || null,
        upline_id: affiliate.referredBy || null,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Affiliate added successfully!');
      fetchAffiliates(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to add affiliate');
      toast.error(err.response?.data?.message || err.message || 'Failed to add affiliate');
      console.error('Add affiliate error:', err.response || err); // Log the full error response
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const updateAffiliate = async (id: string, data: Partial<Omit<Affiliate, 'properties' | 'commission' | 'joinDate' | 'level'>>) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found.');
      }

      await axios.put(`https://uniquestatebackend.gomsolutions.com.ng/api/affiliates/${id}`, {
        name: data.name,
        email: data.email,
        phone: data.phone,
        image: data.image || null,
        upline_id: data.referredBy || null,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Affiliate updated successfully!');
      fetchAffiliates(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update affiliate');
      toast.error(err.response?.data?.message || err.message || 'Failed to update affiliate');
      console.error('Update affiliate error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const deleteAffiliate = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found.');
      }

      await axios.delete(`https://uniquestatebackend.gomsolutions.com.ng/api/affiliates/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Affiliate deleted successfully!');
      fetchAffiliates(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete affiliate');
      toast.error(err.response?.data?.message || err.message || 'Failed to delete affiliate');
      console.error('Delete affiliate error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Commission actions
  const fetchCommissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found.');
      }

      const response = await fetch('https://uniquestatebackend.gomsolutions.com.ng/api/commissions', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch commissions');
      }

      const data = await response.json();
      setCommissions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch commissions');
      console.error('Fetch commissions error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addCommission = async (commission: Omit<CommissionRecord, 'id'>) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found.');
      }

      const response = await fetch('https://uniquestatebackend.gomsolutions.com.ng/api/commissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(commission),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add commission');
      }

      const newCommission = await response.json();
      setCommissions(prev => [...prev, newCommission]);

      // Update realtor's commission and commission history
      const updatedRealtors = realtors.map(realtor => {
        if (realtor.id === newCommission.realtorId) {
          return {
            ...realtor,
            commission: realtor.commission + newCommission.amount,
            commissionHistory: [...realtor.commissionHistory, newCommission],
          };
        }
        return realtor;
      });
      setRealtors(updatedRealtors);

      // Update current user if it's the one who received commission
      if (currentUser && currentUser.id === newCommission.realtorId) {
        setCurrentUser(prev => ({
          ...prev!,
          commission: prev!.commission + newCommission.amount,
          commissionHistory: [...prev!.commissionHistory, newCommission],
        }));
      }

      toast.success('Commission added successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to add commission');
      toast.error(err.message || 'Failed to add commission');
      console.error('Add commission error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCommissionStatus = async (id: string, status: 'pending' | 'paid' | 'cancelled') => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found.');
      }

      const response = await fetch(`https://uniquestatebackend.gomsolutions.com.ng/api/commissions/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update commission status');
      }

      const updatedCommission = await response.json();
      setCommissions(prev => prev.map(c => c.id === id ? updatedCommission : c));

      // Update realtor's commission history
      const updatedRealtors = realtors.map(realtor => {
        if (realtor.id === updatedCommission.realtorId) {
          return {
            ...realtor,
            commissionHistory: realtor.commissionHistory.map(ch =>
              ch.id === updatedCommission.id ? updatedCommission : ch
            ),
          };
        }
        return realtor;
      });
      setRealtors(updatedRealtors);

      // Update current user if it's the one whose commission was updated
      if (currentUser && currentUser.id === updatedCommission.realtorId) {
        setCurrentUser(prev => ({
          ...prev!,
          commissionHistory: prev!.commissionHistory.map(ch =>
            ch.id === updatedCommission.id ? updatedCommission : ch
          ),
        }));
      }

      toast.success('Commission status updated successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to update commission status');
      toast.error(err.message || 'Failed to update commission status');
      console.error('Update commission status error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Dashboard data calculations
  const getTotalCommission = (realtorId: string): number => {
    return commissions
      .filter(c => c.realtorId === realtorId && c.status === 'paid')
      .reduce((sum, c) => sum + c.amount, 0);
  };

  const getMonthlyCommission = (realtorId: string, month: number, year: number): number => {
    return commissions
      .filter((c: CommissionRecord) => {
        const commissionDate = new Date(c.date);
        return c.realtorId === realtorId &&
               c.status === 'paid' &&
               commissionDate.getMonth() + 1 === month && // getMonth() is 0-indexed
               commissionDate.getFullYear() === year;
      })
      .reduce((sum, c) => sum + c.amount, 0);
  };

  const getAffiliatesByLevel = (realtorId: string, level: 1 | 2): Affiliate[] => {
    // This logic needs to be more sophisticated if levels are dynamic
    // For now, assuming level 1 affiliates are directly referred by the realtorId
    // Level 2 would be affiliates referred by level 1 affiliates
    if (level === 1) {
      return affiliates.filter(aff => aff.referredBy === realtorId);
    }
    // For level 2, we'd need to find level 1 affiliates first, then their referrals
    if (level === 2) {
      const level1Affiliates = affiliates.filter(aff => aff.referredBy === realtorId);
      const level2Affiliates: Affiliate[] = [];
      level1Affiliates.forEach(l1Aff => {
        affiliates.filter(aff => aff.referredBy === l1Aff.id).forEach(l2Aff => {
          level2Affiliates.push(l2Aff);
        });
      });
      return level2Affiliates;
    }
    return [];
  };

  const contextValue: RealtorContextType = {
    realtors,
    affiliates,
    currentUser,
    loading,
    error,
    commissions,

    login,
    logout,

    addRealtor,
    updateRealtor,
    deleteRealtor,

    addAffiliate,
    updateAffiliate,
    deleteAffiliate,

    fetchCommissions,
    addCommission,
    updateCommissionStatus,

    getTotalCommission,
    getMonthlyCommission,
    getAffiliatesByLevel,
  };

  return (
    <RealtorContext.Provider value={contextValue}>
      {children}
    </RealtorContext.Provider>
  );
};
